import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DollarSign, Calendar, CheckCircle, Clock } from "lucide-react";
import { format, parseISO, startOfMonth, endOfMonth, subMonths } from "date-fns";

interface Payout {
  id: string;
  period_start: string;
  period_end: string;
  consultation_count: number;
  fee_per_consultation: number;
  amount_cents: number;
  status: string;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
}

interface CompletedAppointment {
  id: string;
  patient_id: string;
  start_time: string;
  end_time: string;
  status: string;
}

const statusBadge: Record<string, { label: string; class: string }> = {
  pending: { label: "Pending", class: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  approved: { label: "Approved", class: "bg-blue-100 text-blue-800 border-blue-200" },
  paid: { label: "Paid", class: "bg-green-100 text-green-800 border-green-200" },
};

const ProviderPayoutView = () => {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [appointments, setAppointments] = useState<CompletedAppointment[]>([]);
  const [finalizedCount, setFinalizedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      const [{ data: payoutData }, { data: apptData }, { data: checklistData }] = await Promise.all([
        supabase
          .from("provider_payouts")
          .select("*")
          .eq("provider_id", user.id)
          .order("period_start", { ascending: false }),
        supabase
          .from("appointments")
          .select("id, patient_id, start_time, end_time, status")
          .eq("provider_id", user.id)
          .eq("status", "completed")
          .order("start_time", { ascending: false }),
        supabase
          .from("session_checklist" as any)
          .select("assessment_id, finalized_at")
          .eq("provider_id", user.id)
          .not("finalized_at", "is", null),
      ]);
      setPayouts((payoutData as Payout[]) || []);
      // Only count appointments that have a finalized session checklist
      const finalizedAssessments = new Set(
        ((checklistData as any[]) || []).map((c: any) => c.assessment_id)
      );
      // For now, show all completed appointments but flag finalized ones
      // Since appointments link to patients (not assessments directly), we include all completed
      // but the "This Month" estimate only counts finalized sessions
      setAppointments((apptData as CompletedAppointment[]) || []);
      setFinalizedCount(finalizedAssessments.size);
      setLoading(false);
    };
    load();
  }, [user]);

  // Current month completed consultations (not yet in a payout)
  const now = new Date();
  const currentMonthStart = startOfMonth(now);
  const currentMonthEnd = endOfMonth(now);

  const currentMonthAppts = useMemo(
    () =>
      appointments.filter((a) => {
        const d = parseISO(a.start_time);
        return d >= currentMonthStart && d <= currentMonthEnd;
      }),
    [appointments, currentMonthStart, currentMonthEnd]
  );

  const defaultFee = 2000; // $20.00 in cents
  const totalEarned = payouts
    .filter((p) => p.status === "paid")
    .reduce((sum, p) => sum + p.amount_cents, 0);
  const totalPending = payouts
    .filter((p) => p.status !== "paid")
    .reduce((sum, p) => sum + p.amount_cents, 0);
  const currentMonthEstimate = currentMonthAppts.length * defaultFee;

  // Note displayed to provider about finalization requirement
  const finalizationNote = "Only sessions with a completed finalization checklist count toward payouts.";

  if (loading) {
    return (
      <div className="space-y-4">
        <h2 className="font-display text-xl text-foreground">Consultation Fees & Payouts</h2>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-muted rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="font-display text-xl text-foreground">Consultation Fees & Payouts</h2>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          {
            label: "Total Earned",
            value: `$${(totalEarned / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
            icon: DollarSign,
            sub: "All time paid",
          },
          {
            label: "Pending Payout",
            value: `$${(totalPending / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
            icon: Clock,
            sub: "Awaiting payment",
          },
          {
            label: "This Month",
            value: `${currentMonthAppts.length} consultations`,
            icon: Calendar,
            sub: `Est. $${(currentMonthEstimate / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
          },
        ].map((kpi) => (
          <Card key={kpi.label} className="border-border bg-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground font-medium">{kpi.label}</p>
                  <p className="text-xl font-bold text-foreground mt-1">{kpi.value}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{kpi.sub}</p>
                </div>
                <kpi.icon size={18} className="text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payout History */}
      {payouts.length > 0 ? (
        <Card className="border-border bg-card">
          <CardContent className="p-0">
            <div className="px-5 pt-4 pb-2">
              <h3 className="text-sm font-semibold text-foreground">Payout History</h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-center">Consultations</TableHead>
                  <TableHead className="text-center">Rate</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map((p) => {
                  const cfg = statusBadge[p.status] || statusBadge.pending;
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="text-sm">
                        {format(parseISO(p.period_start), "MMM d")} – {format(parseISO(p.period_end), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-sm text-center">{p.consultation_count}</TableCell>
                      <TableCell className="text-sm text-center">
                        ${(p.fee_per_consultation / 100).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-sm text-right font-medium">
                        ${(p.amount_cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className={`text-xs ${cfg.class}`}>
                          {p.status === "paid" && <CheckCircle size={10} className="mr-1" />}
                          {cfg.label}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border bg-card">
          <CardContent className="p-8 text-center">
            <DollarSign size={36} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No payouts recorded yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Payouts are generated monthly based on your completed consultations.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Current Month Consultations */}
      {currentMonthAppts.length > 0 && (
        <Card className="border-border bg-card">
          <CardContent className="p-0">
            <div className="px-5 pt-4 pb-2">
              <h3 className="text-sm font-semibold text-foreground">
                This Month's Completed Consultations ({format(now, "MMMM yyyy")})
              </h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead className="text-right">Fee</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentMonthAppts.map((a, i) => (
                  <TableRow key={a.id}>
                    <TableCell className="text-sm text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="text-sm">
                      {format(parseISO(a.start_time), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(parseISO(a.start_time), "h:mm a")}
                    </TableCell>
                    <TableCell className="text-sm text-right font-medium">
                      ${(defaultFee / 100).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProviderPayoutView;
